import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listChatSessions,
  createChatSession,
  getChatSession,
  deleteChatSession,
  streamSessionChat,
  uploadKnowledgeDoc,
  ApiError,
  type ChatSessionMeta,
  type ChatSource,
} from "@/lib/api";
import { md } from "@/lib/markdown";
import { STATUS_HUE } from "@/lib/statusColors";
import { Modal } from "@/components/Modal";
import { DocPreviewModal } from "@/components/DocPreviewModal";

/** Inline-view + office-conversion URLs for a chat source's original file. */
function sourceFileUrls(
  ref: string | null | undefined,
): { url: string; previewUrl?: string } | null {
  if (!ref) return null;
  if (ref.startsWith("doc:")) {
    const id = ref.slice(4);
    return {
      url: `/api/docs/files/${id}/content`,
      previewUrl: `/api/docs/files/${id}/preview`,
    };
  }
  // Ticket attachments stream directly; no office converter for them.
  if (ref.startsWith("attachment:")) {
    return { url: `/api/board/attachments/${ref.slice(11)}` };
  }
  return null;
}

/* ════════════════════════════════════════════════════════════════════════
   Constants
   ════════════════════════════════════════════════════════════════════════ */
interface SuggestedPrompt {
  icon: string;
  text: string;
  category: keyof typeof CATEGORY_COLORS;
}

const SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  {
    icon: "fa-file-lines",
    text: "Summarise the key points of our most recently uploaded documents",
    category: "docs",
  },
  {
    icon: "fa-scale-balanced",
    text: "What do our contracts and agreements say about payment terms?",
    category: "docs",
  },
  {
    icon: "fa-user-plus",
    text: "What does our onboarding documentation cover? List the steps",
    category: "team",
  },
  {
    icon: "fa-coins",
    text: "Find any figures on pricing, costs, or budgets in our documents",
    category: "finance",
  },
  {
    icon: "fa-triangle-exclamation",
    text: "What risks or open issues are flagged across our documents?",
    category: "analysis",
  },
  {
    icon: "fa-magnifying-glass",
    text: "Search everything we have about a customer or project — just name it",
    category: "knowledge",
  },
];

const CATEGORY_COLORS = {
  docs: STATUS_HUE.green,
  team: STATUS_HUE.blue,
  finance: STATUS_HUE.amber,
  knowledge: STATUS_HUE.purple,
  analysis: STATUS_HUE.orange,
} as const;

/* ════════════════════════════════════════════════════════════════════════
   Types
   ════════════════════════════════════════════════════════════════════════ */
interface UiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[];
  pending?: boolean;
  /** Live activity label ("Searching: …") shown as a shimmer while pending. */
  thinkingText?: string;
  /** Searches the agent has run for this answer — kept as a small activity trail. */
  activity?: string[];
}

interface PendingFile {
  id: string;
  name: string;
  status: "loading" | "done" | "error";
}

let uid = 0;
const nextId = () => `m${++uid}`;

/* ════════════════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════════════════ */
export function ChatPage() {
  const qc = useQueryClient();
  const sessionsQuery = useQuery({ queryKey: ["chat-sessions"], queryFn: listChatSessions });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ChatSessionMeta | null>(null);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [fadeKey, setFadeKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const activeSessionTitle =
    sessionsQuery.data?.find((s) => s.id === activeId)?.title || "Chat";

  async function loadSession(id: string) {
    abortRef.current?.abort();
    setActiveId(id);
    setLoadingSession(true);
    try {
      const s = await getChatSession(id);
      setMessages(
        s.messages.map((m) => ({
          id: nextId(),
          role: m.role,
          content: m.content,
          sources: m.sources ?? undefined,
        })),
      );
      setFadeKey((k) => k + 1);
    } catch {
      setMessages([]);
    } finally {
      setLoadingSession(false);
    }
  }

  function handleNewChat() {
    abortRef.current?.abort();
    setActiveId(null);
    setMessages([]);
    setFadeKey((k) => k + 1);
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteChatSession(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["chat-sessions"] });
      setConfirmDelete(null);
      if (id === activeId) handleNewChat();
    },
  });

  async function sendMessage(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    setBusy(true);

    let sessionId = activeId;
    try {
      if (!sessionId) {
        const created = await createChatSession();
        sessionId = created.id;
        setActiveId(created.id);
      }
    } catch (err) {
      setBusy(false);
      setMessages((m) => [
        ...m,
        { id: nextId(), role: "user", content: question },
        {
          id: nextId(),
          role: "assistant",
          content: `⚠️ ${err instanceof ApiError ? err.message : "Could not start a chat"}`,
        },
      ]);
      return;
    }

    const answerId = nextId();
    setMessages((m) => [
      ...m,
      { id: nextId(), role: "user", content: question },
      {
        id: answerId,
        role: "assistant",
        content: "",
        pending: true,
        thinkingText: "Thinking…",
        activity: [],
      },
    ]);

    const abort = new AbortController();
    abortRef.current = abort;
    const patch = (fn: (msg: UiMessage) => UiMessage) =>
      setMessages((m) => m.map((x) => (x.id === answerId ? fn(x) : x)));

    try {
      await streamSessionChat(
        sessionId,
        question,
        (e) => {
          if (e.type === "token") {
            patch((x) => ({
              ...x,
              content: x.content + e.text,
              thinkingText: undefined,
            }));
          } else if (e.type === "status") {
            // A new search round: anything streamed so far was narration.
            patch((x) => ({
              ...x,
              content: "",
              thinkingText: e.text,
              activity: [...(x.activity ?? []), e.text],
            }));
          } else if (e.type === "sources") {
            patch((x) => ({ ...x, sources: e.sources }));
          } else if (e.type === "error") {
            patch((x) => ({
              ...x,
              content: `⚠️ ${e.message}`,
              thinkingText: undefined,
              pending: false,
            }));
          } else if (e.type === "done") {
            patch((x) => ({ ...x, thinkingText: undefined, pending: false }));
          }
        },
        abort.signal,
      );
    } catch (err) {
      if (!abort.signal.aborted) {
        patch((x) => ({
          ...x,
          content:
            x.content ||
            `⚠️ ${err instanceof ApiError ? err.message : "Chat request failed"}`,
          thinkingText: undefined,
          pending: false,
        }));
      }
    } finally {
      if (abortRef.current === abort) abortRef.current = null;
      patch((x) => ({ ...x, thinkingText: undefined, pending: false }));
      setBusy(false);
      qc.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
  }

  async function uploadFile(file: File) {
    const id = nextId();
    setFiles((f) => [...f, { id, name: file.name, status: "loading" }]);
    try {
      await uploadKnowledgeDoc(file);
      setFiles((f) => f.map((x) => (x.id === id ? { ...x, status: "done" } : x)));
      setTimeout(() => setFiles((f) => f.filter((x) => x.id !== id)), 4000);
    } catch {
      setFiles((f) => f.map((x) => (x.id === id ? { ...x, status: "error" } : x)));
    }
  }

  return (
    // Full-bleed route (see AppLayout): fills the viewport with no page
    // scroll; only the conversation pane and history rail scroll internally.
    <div className="flex h-screen overflow-hidden">
      <ChatMain
        title={activeSessionTitle}
        messages={messages}
        loading={loadingSession}
        fadeKey={fadeKey}
        files={files}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
        busy={busy}
        onSend={sendMessage}
        onStop={() => abortRef.current?.abort()}
        onPickFile={uploadFile}
        onRemoveFile={(id) => setFiles((f) => f.filter((x) => x.id !== id))}
        onSuggestion={(text) => void sendMessage(text)}
      />
      <SidebarPanel
        collapsed={sidebarCollapsed}
        sessions={sessionsQuery.data ?? []}
        loading={sessionsQuery.isLoading}
        activeId={activeId}
        onSelect={(id) => void loadSession(id)}
        onNewChat={handleNewChat}
        onDelete={(s) => setConfirmDelete(s)}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete conversation"
        footer={
          <>
            <button
              type="button"
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: "var(--color-danger)" }}
              disabled={deleteMutation.isPending}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          Delete{" "}
          <strong className="text-text-heading">
            “{confirmDelete?.title || "Untitled"}”
          </strong>
          ? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Chat main pane
   ════════════════════════════════════════════════════════════════════════ */
function ChatMain({
  title,
  messages,
  loading,
  fadeKey,
  files,
  sidebarCollapsed,
  onToggleSidebar,
  busy,
  onSend,
  onStop,
  onPickFile,
  onRemoveFile,
  onSuggestion,
}: {
  title: string;
  messages: UiMessage[];
  loading: boolean;
  fadeKey: number;
  files: PendingFile[];
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onPickFile: (file: File) => void;
  onRemoveFile: (id: string) => void;
  onSuggestion: (text: string) => void;
}) {
  const showWelcome = messages.length === 0 && !loading;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const lastContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, lastContent]);

  return (
    <section
      className="flex flex-col flex-1 min-w-0 overflow-hidden"
      style={{ height: "100%" }}
    >
      <header
        className="flex items-center gap-2 px-5 py-3"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <span className="text-sm font-semibold text-text-heading truncate">{title}</span>
        <span
          className="ml-2 hidden sm:inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[0.6rem] font-semibold"
          style={{
            background: "var(--color-bg-secondary)",
            color: "var(--color-text-secondary)",
            border: "1px solid var(--color-border)",
          }}
        >
          <i className="fa-solid fa-book text-[0.55rem]" />
          Knowledge base
        </span>
        <button
          type="button"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? "Show history" : "Hide history"}
          className="ml-auto grid place-items-center w-8 h-8 rounded transition-colors text-text-muted hover:text-text-heading"
          style={{
            background: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border)",
          }}
        >
          <i className="fa-solid fa-clock-rotate-left text-[0.7rem]" />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        {loading ? (
          <div key="loader" className="grid place-items-center py-20 chat-fade">
            <i className="fa-solid fa-circle-notch fa-spin text-text-muted text-lg" />
          </div>
        ) : (
          <div key={fadeKey} className="chat-fade">
            {showWelcome ? (
              <WelcomeState onSuggestion={onSuggestion} />
            ) : (
              <div className="mx-auto max-w-3xl space-y-4">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div
          className="px-4 py-2 flex flex-wrap gap-2"
          style={{ borderTop: "1px solid var(--color-border-light)" }}
        >
          {files.map((f) => (
            <FileTile key={f.id} file={f} onRemove={() => onRemoveFile(f.id)} />
          ))}
        </div>
      )}

      <ChatInputBar
        busy={busy}
        onSubmit={onSend}
        onStop={onStop}
        onAttach={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.txt,.md,.csv,.json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPickFile(f);
          e.target.value = "";
        }}
      />
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Welcome / empty state
   ════════════════════════════════════════════════════════════════════════ */
function WelcomeState({ onSuggestion }: { onSuggestion: (text: string) => void }) {
  return (
    <div className="grid place-items-center text-center py-8">
      <div className="max-w-2xl">
        <span
          className="grid place-items-center w-16 h-16 rounded-2xl mx-auto mb-4"
          style={{
            background: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border)",
          }}
        >
          <img src="/logo.png" alt="Vernias" className="w-10 h-10 object-contain" />
        </span>
        <h1 className="page-title mb-2">
          Vernias Chat
        </h1>
        <p className="text-sm text-text-secondary leading-relaxed mb-6 max-w-xl mx-auto">
          Every file uploaded across the portal — the document hub, ticket attachments,
          or right here — is indexed and searchable. Ask anything; answers cite the
          documents they come from.
        </p>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <CapabilityBadge color={STATUS_HUE.green} icon="fa-folder-open" label="Document Hub" />
          <CapabilityBadge color={STATUS_HUE.blue} icon="fa-columns" label="Ticket Attachments" />
          <CapabilityBadge color={STATUS_HUE.purple} icon="fa-quote-left" label="Cited Answers" />
          <CapabilityBadge color={STATUS_HUE.amber} icon="fa-bolt" label="Live Indexing" />
        </div>

        <div className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
          {SUGGESTED_PROMPTS.map((p) => (
            <button
              key={p.text}
              type="button"
              onClick={() => onSuggestion(p.text)}
              className="suggested-prompt text-left text-xs px-3 py-2.5 rounded transition-colors flex items-start gap-2 group"
            >
              <i
                className={`fa-solid ${p.icon} text-[0.7rem] mt-0.5 shrink-0`}
                style={{ color: CATEGORY_COLORS[p.category] }}
              />
              <span className="leading-relaxed group-hover:text-text-heading transition-colors">
                {p.text}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CapabilityBadge({
  color,
  icon,
  label,
}: {
  color: string;
  icon: string;
  label: string;
}) {
  return (
    <span
      className="text-[0.7rem] font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
      style={{
        background: `${color}15`,
        color,
        border: `1px solid ${color}40`,
      }}
    >
      <i className={`fa-solid ${icon} text-[0.6rem]`} />
      {label}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Message bubble
   ════════════════════════════════════════════════════════════════════════ */
function MessageBubble({ message }: { message: UiMessage }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[75%] px-4 py-2.5 rounded-xl text-sm leading-relaxed"
          style={{
            background: "var(--color-accent)",
            color: "var(--color-on-accent)",
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  // No avatar/bubble — the assistant reply reads as flowing prose.
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {message.activity && message.activity.length > 0 && (
        <ActivityTrail activity={message.activity} pending={!!message.pending} />
      )}
      {message.thinkingText ? (
        <ThinkingBubble text={message.thinkingText} />
      ) : (
        <div
          className="text-sm leading-relaxed"
          // Full-strength ink for the answer body — the mid-tone --color-text
          // was hard to read at this size, especially on the light theme.
          style={{ wordBreak: "break-word", color: "var(--color-text-heading)" }}
          dangerouslySetInnerHTML={{ __html: md(message.content || "…") }}
        />
      )}
      {message.sources && message.sources.length > 0 && (
        <Sources sources={message.sources} />
      )}
    </div>
  );
}

/** The searches run for this answer, folded into one quiet line. */
function ActivityTrail({ activity, pending }: { activity: string[]; pending: boolean }) {
  const [open, setOpen] = useState(false);
  if (pending && activity.length <= 1) return null;
  return (
    <div className="text-[0.7rem] text-text-muted">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 hover:text-text-secondary transition-colors"
      >
        <i className={`fa-solid fa-chevron-${open ? "down" : "right"} text-[0.5rem]`} />
        <i className="fa-solid fa-magnifying-glass text-[0.55rem]" />
        {activity.length} search{activity.length === 1 ? "" : "es"}
      </button>
      {open && (
        <ul className="mt-1 ml-4 space-y-0.5">
          {activity.map((a, i) => (
            <li key={i} className="truncate">
              {a}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// "Thinking…" — the status label itself shimmers (a soft light sweep across
// the words) instead of bouncing dots. Look lives in `.cypher-text-shimmer`.
function ThinkingBubble({ text }: { text: string }) {
  return <span className="cypher-text-shimmer text-sm">{text}</span>;
}

function Sources({ sources }: { sources: ChatSource[] }) {
  const [openExcerpt, setOpenExcerpt] = useState<number | null>(null);
  const [preview, setPreview] = useState<{
    name: string;
    url: string;
    previewUrl?: string;
  } | null>(null);

  const excerptsOf = (s: ChatSource): string[] =>
    s.excerpts ?? (s.excerpt ? [s.excerpt] : []);

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {sources.map((s) => {
          const urls = sourceFileUrls(s.ref);
          const active = openExcerpt === s.n;
          return (
            <button
              key={s.n}
              type="button"
              onClick={() =>
                urls
                  ? setPreview({ name: s.title, ...urls })
                  : setOpenExcerpt(active ? null : s.n)
              }
              title={urls ? `Open ${s.title}` : s.title}
              className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition-colors hover:border-accent"
              style={{
                background: active ? "var(--color-accent-muted)" : "var(--color-bg-secondary)",
                color: active ? "var(--color-accent)" : "var(--color-text-secondary)",
                border: "1px solid var(--color-border)",
              }}
            >
              <i
                className={`fa-solid ${urls ? "fa-arrow-up-right-from-square" : "fa-file-lines"} text-[0.55rem]`}
              />
              [{s.n}] {s.title.length > 34 ? s.title.slice(0, 34) + "…" : s.title}
            </button>
          );
        })}
      </div>

      {openExcerpt != null && (
        <div
          className="mt-2 space-y-2 rounded p-2.5 text-xs leading-relaxed text-text-secondary"
          style={{
            background: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border-light)",
          }}
        >
          {excerptsOf(sources.find((s) => s.n === openExcerpt) ?? ({} as ChatSource)).map(
            (e, i) => (
              <p key={i} className={i > 0 ? "border-t border-border-light pt-2" : ""}>
                {e}
              </p>
            ),
          )}
        </div>
      )}

      {preview && (
        <DocPreviewModal
          name={preview.name}
          url={preview.url}
          previewUrl={preview.previewUrl}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Input bar
   ════════════════════════════════════════════════════════════════════════ */
function ChatInputBar({
  busy,
  onSubmit,
  onStop,
  onAttach,
}: {
  busy: boolean;
  onSubmit: (text: string) => void;
  onStop: () => void;
  onAttach: () => void;
}) {
  const [text, setText] = useState("");
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    onSubmit(trimmed);
    setText("");
    if (taRef.current) taRef.current.style.height = "auto";
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleInput() {
    if (!taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = Math.min(taRef.current.scrollHeight, 200) + "px";
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="px-4 py-3"
      style={{ borderTop: "1px solid var(--color-border)" }}
    >
      <div
        className="mx-auto flex max-w-3xl items-end gap-2 rounded-xl px-2 py-2"
        style={{
          background: "var(--color-bg-secondary)",
          border: "1px solid var(--color-border)",
        }}
      >
        <button
          type="button"
          onClick={onAttach}
          title="Add a document to the knowledge base (PDF, DOCX, TXT, CSV, MD)"
          className="grid place-items-center w-9 h-9 rounded transition-colors text-text-muted hover:text-text-heading hover:bg-bg-card-hover shrink-0"
        >
          <i className="fa-solid fa-paperclip text-sm" />
        </button>
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            handleInput();
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Ask about your documents, tickets, customers…"
          className="flex-1 bg-transparent resize-none border-0 outline-none text-sm leading-relaxed py-1.5 text-text placeholder:text-text-muted"
          style={{ minHeight: 24, maxHeight: 200 }}
          autoComplete="off"
          spellCheck
        />
        {busy ? (
          <button
            type="button"
            onClick={onStop}
            title="Stop generating"
            className="grid place-items-center w-9 h-9 rounded-full transition-colors shrink-0"
            style={{
              background: "var(--color-bg-tertiary)",
              color: "var(--color-text-heading)",
              border: "1px solid var(--color-border)",
            }}
          >
            <i className="fa-solid fa-stop text-xs" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!text.trim()}
            title="Send (Enter)"
            className="grid place-items-center w-9 h-9 rounded-full transition-colors shrink-0"
            style={{
              background: text.trim() ? "var(--color-accent)" : "var(--color-bg-tertiary)",
              color: text.trim() ? "var(--color-on-accent)" : "var(--color-text-muted)",
            }}
          >
            <i className="fa-solid fa-arrow-up text-sm" />
          </button>
        )}
      </div>
    </form>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   File tile (above input bar while indexing)
   ════════════════════════════════════════════════════════════════════════ */
function FileTile({ file, onRemove }: { file: PendingFile; onRemove: () => void }) {
  const statusIcon =
    file.status === "loading" ? (
      <i className="fa-solid fa-spinner fa-spin text-[0.6rem]" />
    ) : file.status === "done" ? (
      <i
        className="fa-solid fa-circle-check text-[0.65rem]"
        style={{ color: "var(--color-success)" }}
      />
    ) : (
      <i
        className="fa-solid fa-circle-exclamation text-[0.65rem]"
        style={{ color: "var(--color-danger)" }}
      />
    );
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-[0.7rem]"
      style={{
        background: "var(--color-bg-secondary)",
        border: `1px solid ${
          file.status === "done"
            ? "color-mix(in srgb, var(--color-success) 35%, transparent)"
            : file.status === "error"
              ? "color-mix(in srgb, var(--color-danger) 35%, transparent)"
              : "var(--color-border)"
        }`,
      }}
    >
      <i className="fa-solid fa-file-lines text-[0.65rem] text-text-muted" />
      <span className="text-text-secondary truncate max-w-[200px]">{file.name}</span>
      {statusIcon}
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        className="text-text-muted hover:text-text-heading transition-colors"
      >
        <i className="fa-solid fa-xmark text-[0.55rem]" />
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Sidebar (right) — collapsible chat history, grouped by date
   ════════════════════════════════════════════════════════════════════════ */
function SidebarPanel({
  collapsed,
  sessions,
  loading,
  activeId,
  onSelect,
  onNewChat,
  onDelete,
}: {
  collapsed: boolean;
  sessions: ChatSessionMeta[];
  loading: boolean;
  activeId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onDelete: (s: ChatSessionMeta) => void;
}) {
  const groups = useMemo(() => groupSessionsByDate(sessions), [sessions]);

  return (
    <aside
      className="overflow-hidden flex flex-col transition-all duration-300"
      style={{
        width: collapsed ? 0 : 280,
        minWidth: collapsed ? 0 : 280,
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? "none" : "auto",
        height: "100%",
        background: "var(--color-bg-secondary)",
        borderLeft: collapsed ? "none" : "1px solid var(--color-border)",
      }}
    >
      <header
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <i className="fa-solid fa-clock-rotate-left text-[0.75rem] text-text-muted" />
        <span className="text-sm font-semibold text-text-heading flex-1">Chat History</span>
        <button
          type="button"
          onClick={onNewChat}
          title="New chat"
          className="grid place-items-center w-8 h-8 rounded transition-colors text-text-muted hover:text-text-heading hover:bg-bg-card-hover"
          style={{ border: "1px solid var(--color-border)" }}
        >
          <i className="fa-solid fa-plus text-[0.7rem]" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto py-2">
        {loading ? (
          <div className="px-4 py-6 text-center text-text-muted text-xs">
            <i className="fa-solid fa-spinner fa-spin mr-1" />
            Loading…
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-text-muted leading-relaxed">
            No conversations yet.
            <br />
            Click <strong className="text-text-heading">+</strong> to start.
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.label} className="mb-2">
              <div className="px-4 py-1.5 text-[0.6rem] uppercase tracking-wider font-semibold text-text-muted">
                {g.label}
              </div>
              {g.items.map((s) => {
                const title = s.title || "New Chat";
                const isActive = s.id === activeId;
                return (
                  <div
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSelect(s.id);
                    }}
                    className="group flex items-center gap-2 px-3 py-2 mx-2 rounded cursor-pointer transition-colors"
                    style={{
                      background: isActive ? "var(--color-accent-muted)" : "transparent",
                      color: isActive
                        ? "var(--color-text-heading)"
                        : "var(--color-text-secondary)",
                    }}
                  >
                    <i className="fa-solid fa-message text-[0.6rem] text-text-muted" />
                    <span className="flex-1 min-w-0 truncate text-xs">{title}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(s);
                      }}
                      aria-label={`Delete ${title}`}
                      title="Delete"
                      className="chat-delete-btn grid place-items-center w-6 h-6 rounded text-[0.65rem] opacity-0 group-hover:opacity-100 transition-colors"
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

function groupSessionsByDate(sessions: ChatSessionMeta[]): Array<{
  label: string;
  items: ChatSessionMeta[];
}> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const lastWeek = today - 7 * 86_400_000;

  const groups: Array<{ label: string; items: ChatSessionMeta[] }> = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "Last 7 days", items: [] },
    { label: "Older", items: [] },
  ];

  sessions.forEach((s) => {
    const t = new Date(s.updatedAt ?? s.createdAt);
    const day = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
    if (day >= today) groups[0].items.push(s);
    else if (day >= yesterday) groups[1].items.push(s);
    else if (day >= lastWeek) groups[2].items.push(s);
    else groups[3].items.push(s);
  });

  return groups.filter((g) => g.items.length > 0);
}
